/**
 * Reads `message` / `Message` from a Tal-Api GeneralResponse-style JSON body.
 */
export function readGeneralResponseMessage(payload: unknown): string {
  if (!payload || typeof payload !== 'object') {
    return '';
  }
  const o = payload as Record<string, unknown>;
  const raw = o['message'] ?? o['Message'];
  if (typeof raw !== 'string') {
    return '';
  }
  return raw.trim();
}

export function readGeneralResponseSuccess(payload: unknown): boolean | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const o = payload as Record<string, unknown>;
  if (typeof o['success'] === 'boolean') {
    return o['success'];
  }
  if (typeof o['Success'] === 'boolean') {
    return o['Success'];
  }
  return undefined;
}

/**
 * Reads API message from an Angular HttpClient error (e.g. 400 BadRequest with GeneralResponse body).
 */
export function readHttpErrorApiMessage(err: unknown): string {
  if (!err || typeof err !== 'object') {
    return '';
  }
  const httpErr = err as { error?: unknown };
  const body = httpErr.error;
  if (typeof body === 'string') {
    const t = body.trim();
    if (!t) {
      return '';
    }
    try {
      const parsed = JSON.parse(t) as { message?: string; Message?: string };
      return (parsed.message ?? parsed.Message ?? '').toString().trim();
    } catch {
      return t;
    }
  }
  if (body && typeof body === 'object') {
    return readGeneralResponseMessage(body);
  }
  return '';
}

function asRecord(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  return payload as Record<string, unknown>;
}

function parseMaybeJson(payload: unknown): unknown {
  if (typeof payload !== 'string') {
    return payload;
  }
  const t = payload.trim();
  if (!t) {
    return payload;
  }
  try {
    return JSON.parse(t);
  } catch {
    return payload;
  }
}

function readResourceCode(payload: unknown): string {
  const o = asRecord(parseMaybeJson(payload));
  if (!o) {
    return '';
  }
  const raw = o['resource'] ?? o['Resource'];
  if (raw == null) {
    return '';
  }
  return String(raw).trim();
}

/**
 * Portal/Dashboard ForgetPassword returns 400 with
 * `{ message: "Bad Request", resource: "400", success: false }` when the email is not registered.
 */
export function isUnregisteredForgotPasswordFailure(
  err?: { status?: number; error?: unknown } | null,
  body?: unknown
): boolean {
  const status = err?.status;
  if (status === 400 || status === 404) {
    return true;
  }

  const payload = parseMaybeJson(body ?? err?.error);
  const message = readGeneralResponseMessage(payload).toLowerCase();
  const resource = readResourceCode(payload);
  const success = readGeneralResponseSuccess(payload);

  if (resource === '400') {
    return true;
  }
  if (message === 'bad request') {
    return true;
  }
  if (success === false && (status === 400 || status === 404 || resource === '400')) {
    return true;
  }
  return false;
}
